import { useEffect, useRef, useState } from "react";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CloseIcon from "@mui/icons-material/Close";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import { Drawer, IconButton, Tooltip } from "@mui/material";
import AssistantMessage from "./AssistantMessage";
import { assistantConfigured } from "./assistantApi";
import useAssistantChat from "./useAssistantChat";

const SUGGESTIONS = {
  admin: [
    "Which clients have documents outstanding?",
    "What appointments do I have this week?",
    "Summarise this client for me",
    "Show overdue tasks assigned to me",
  ],
  partner: [
    "Which of my clients are waiting on documents?",
    "What have I earned this month?",
    "Who is booked in this week?",
    "Which leads still need my action?",
  ],
};

const Welcome = ({ surface, onPick, disabled }) => (
  <div className="flex h-full flex-col justify-center px-1 py-6">
    <span className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-600 text-white shadow-md">
      <AutoAwesomeOutlinedIcon />
    </span>
    <h3 className="m-0 mt-3 text-center text-base font-bold text-slate-900">
      How can I help today?
    </h3>
    <p className="m-0 mt-1 text-center text-sm text-slate-500">
      Ask about clients, documents, appointments or payments. I'll ask before changing anything.
    </p>
    <div className="mt-5 grid gap-2">
      {SUGGESTIONS[surface].map((suggestion) => (
        <button
          key={suggestion}
          type="button"
          disabled={disabled}
          onClick={() => onPick(suggestion)}
          className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-left text-sm text-slate-700 cursor-pointer transition-colors hover:border-teal-300 hover:bg-teal-50/50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {suggestion}
        </button>
      ))}
    </div>
  </div>
);

/** The assistant's side panel: one conversation, streamed, with approvals for any change. */
const AssistantPanel = ({ open, onClose, surface = "admin" }) => {
  const chat = useAssistantChat(surface);
  const [draft, setDraft] = useState("");
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const connected = assistantConfigured();
  const streaming = chat.status === "streaming";

  useEffect(() => {
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [chat.messages]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 150);
  }, [open]);

  const submit = (text = draft) => {
    if (!text.trim() || streaming || !connected) return;
    chat.send(text);
    setDraft("");
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100vw", sm: 440 }, backgroundColor: "#f8fafc" } }}
    >
      <div className="flex h-full flex-col">
        <header className="flex items-center justify-between gap-2 border-b border-slate-200 bg-white px-4 py-3">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 text-white">
              <AutoAwesomeOutlinedIcon sx={{ fontSize: 18 }} />
            </span>
            <div>
              <p className="m-0 text-sm font-bold text-slate-900 leading-tight">Kothar Assistant</p>
              <p className="m-0 text-[11px] text-slate-500 leading-tight">
                {connected ? "Beta · checks with you before changes" : "Not connected yet"}
              </p>
            </div>
          </div>
          <div className="flex items-center">
            <Tooltip title="New conversation">
              <span>
                <IconButton
                  size="small"
                  onClick={chat.reset}
                  disabled={!chat.messages.length}
                  aria-label="New conversation"
                >
                  <EditNoteOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton size="small" onClick={onClose} aria-label="Close assistant">
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </header>

        {connected ? null : (
          <div className="border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800">
            The assistant service isn't connected in this environment yet. Set{" "}
            <code className="font-mono">REACT_APP_AI_API_URL</code> to switch it on.
          </div>
        )}

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4">
          {chat.messages.length ? (
            <div className="space-y-4">
              {chat.messages.map((message) => (
                <AssistantMessage key={message.id} message={message} onDecide={chat.decide} />
              ))}
            </div>
          ) : (
            <Welcome surface={surface} onPick={submit} disabled={!connected} />
          )}
        </div>

        <footer className="border-t border-slate-200 bg-white px-3 pb-3 pt-2.5">
          <div className="flex items-end gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 focus-within:border-teal-400 focus-within:ring-2 focus-within:ring-teal-100">
            <textarea
              ref={inputRef}
              rows={1}
              value={draft}
              disabled={!connected}
              placeholder={connected ? "Ask anything about your work…" : "Assistant not connected"}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  submit();
                }
              }}
              className="max-h-32 min-h-[24px] flex-1 border-0 bg-transparent p-0 text-sm text-slate-800 outline-none placeholder:text-slate-400 disabled:cursor-not-allowed"
              style={{ fieldSizing: "content" }}
              aria-label="Message the assistant"
            />
            {streaming ? (
              <IconButton
                size="small"
                onClick={chat.stop}
                aria-label="Stop"
                sx={{ bgcolor: "#0f172a", color: "white", "&:hover": { bgcolor: "#1e293b" } }}
              >
                <StopRoundedIcon fontSize="small" />
              </IconButton>
            ) : (
              <IconButton
                size="small"
                onClick={() => submit()}
                disabled={!draft.trim() || !connected}
                aria-label="Send"
                sx={{
                  bgcolor: "#0f766e",
                  color: "white",
                  "&:hover": { bgcolor: "#115e59" },
                  "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" },
                }}
              >
                <SendRoundedIcon fontSize="small" />
              </IconButton>
            )}
          </div>
          <p className="m-0 mt-1.5 text-center text-[10px] text-slate-400">
            Enter to send · Shift+Enter for a new line · Answers can be wrong, check before acting
          </p>
        </footer>
      </div>
    </Drawer>
  );
};

export default AssistantPanel;
